import { type FormEvent, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/stores/auth";
import {Glass} from "@/components/glass";

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (user) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      const dest = (location.state as { from?: string } | null)?.from ?? "/";
      navigate(dest, { replace: true });
    } catch {
      /* handled by store */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid place-items-center min-h-screen px-4">
      <Glass as="form" className="p-8 w-full max-w-sm space-y-5" onSubmit={onSubmit}>
        <header className="space-y-1">
          <div className="kicker">Bar-Ilan University · MHM</div>
          <h1 className="text-2xl font-semibold tracking-tight">MHM Pipeline</h1>
          <p className="text-sm muted">Sign in to continue.</p>
        </header>

        <label className="block space-y-1">
          <span className="text-sm muted">Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-glass"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-sm muted">Password</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-glass"
          />
        </label>

        {error && (
          <p className="text-sm text-danger" role="alert">{error}</p>
        )}

        <button type="submit" disabled={submitting} className="button-primary w-full">
          {submitting ? "Signing in…" : "Sign in"}
        </button>

        <div className="flex items-center justify-between text-xs muted">
          <Link to="/forgot-password" className="hover:text-ink underline-offset-2 hover:underline">
            Forgot password?
          </Link>
          <span>
            Don't have an account?{" "}
            <Link to="/request-access" className="hover:text-ink underline-offset-2 hover:underline">
              Request access
            </Link>
          </span>
        </div>
      </Glass>
    </div>
  );
}
